import { useState, useEffect } from 'react';
import { useAdminSettings, useUpdateAdminSettings } from '@theraply/api-client';

export default function SettingsPage() {
  const { data, isLoading, error, refetch } = useAdminSettings();
  const updateSettings = useUpdateAdminSettings();
  const [editedValues, setEditedValues] = useState<Record<string, string>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Initialize form values when data loads
  useEffect(() => {
    if (data?.settings) {
      const values: Record<string, string> = {};
      for (const [key, setting] of Object.entries(data.settings)) {
        values[key] = setting.value;
      }
      setEditedValues(values);
      setHasChanges(false);
    }
  }, [data]);

  const handleChange = (key: string, value: string) => {
    setEditedValues((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
    setSaveSuccess(false);
  };

  const handleSave = () => {
    if (!data?.settings || !hasChanges) return;

    // Find changed values
    const changes: Record<string, string> = {};
    for (const [key, value] of Object.entries(editedValues)) {
      if (data.settings[key]?.value !== value) {
        changes[key] = value;
      }
    }

    if (Object.keys(changes).length === 0) return;

    if (!confirm('Are you sure you want to save these settings changes?')) return;

    updateSettings.mutate(changes, {
      onSuccess: () => {
        setHasChanges(false);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      },
    });
  };

  const handleReset = () => {
    if (data?.settings) {
      const values: Record<string, string> = {};
      for (const [key, setting] of Object.entries(data.settings)) {
        values[key] = setting.value;
      }
      setEditedValues(values);
      setHasChanges(false);
    }
  };

  const formatSettingLabel = (key: string): string => {
    return key
      .split(/[._]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-semibold">Error</h3>
          <p className="text-red-600">
            {error instanceof Error ? error.message : 'Failed to load settings'}
          </p>
          <button
            onClick={() => refetch()}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const settingKeys = Object.keys(data?.settings || {}).sort();

  return (
    <div>
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">System Settings</h1>
          <p className="mt-1 text-sm text-gray-500">
            Configure platform-wide settings and preferences.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          {hasChanges && (
            <button
              onClick={handleReset}
              className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Reset
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={!hasChanges || updateSettings.isPending}
            className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {updateSettings.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Success message */}
      {saveSuccess && (
        <div className="mt-4 rounded-md bg-green-50 p-4">
          <p className="text-sm text-green-800">Settings saved successfully.</p>
        </div>
      )}

      {/* Error message */}
      {updateSettings.isError && (
        <div className="mt-4 rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-800">
            Failed to save settings. {updateSettings.error instanceof Error ? updateSettings.error.message : ''}
          </p>
        </div>
      )}

      {/* Settings Form */}
      <div className="mt-8">
        {settingKeys.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-8 text-center text-gray-500">
            No settings configured yet. Settings will appear here once they are added to the platform.
          </div>
        ) : (
          <div className="bg-white shadow rounded-lg divide-y divide-gray-200">
            {settingKeys.map((key) => {
              const setting = data!.settings[key];
              const isMultiline = (setting.value || '').length > 100 || (setting.value || '').includes('\n');

              return (
                <div key={key} className="px-6 py-4">
                  <label htmlFor={`setting-${key}`} className="block text-sm font-medium text-gray-900">
                    {formatSettingLabel(key)}
                  </label>
                  <p className="text-xs text-gray-500 mt-0.5">Key: {key}</p>
                  {isMultiline ? (
                    <textarea
                      id={`setting-${key}`}
                      value={editedValues[key] || ''}
                      onChange={(e) => handleChange(key, e.target.value)}
                      rows={3}
                      className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                  ) : (
                    <input
                      id={`setting-${key}`}
                      type="text"
                      value={editedValues[key] || ''}
                      onChange={(e) => handleChange(key, e.target.value)}
                      className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                  )}
                  {setting.updatedAt && (
                    <p className="text-xs text-gray-400 mt-1">
                      Last updated: {new Date(setting.updatedAt).toLocaleString()}
                      {setting.updatedBy && ` by admin #${setting.updatedBy}`}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
